/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject, useEffect } from 'react'
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik'
import styled from 'styled-components'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm, Theme } from '@nexigendigital/nxui'
import { useInitialiseApplicationQuery, useRegisterMutation, useSecurityQuestionsQuery } from 'api/app'
import { setCartItem, setCartOpen } from 'store/slices/shopSlice'
import { SignUpFields } from 'models/app'
import { getStatesByCountryCode, regex } from 'helpers/utils'
import { useAppDispatch, useAppSelector } from 'store/hooks'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    form: RefObject<FormikProps<SignUpFields>>
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function SignupForm({ form }: Props) {
    const dispatch = useAppDispatch()
    const { cartItem } = useAppSelector((state) => state.shop)
    const [register, { isError, isSuccess: registerSuccess }] = useRegisterMutation()
    const { data: securityQuestions, isSuccess } = useSecurityQuestionsQuery(undefined)
    const { refetch } = useInitialiseApplicationQuery(undefined, {
        skip: !registerSuccess
    })

    function UpdateState() {
        const { values, initialValues, setFieldValue } = useFormikContext<{ country: string, state: string }>()

        useEffect(() => {
            if (values.country !== initialValues.country && values.state === initialValues.state) {
                setFieldValue('state', getStatesByCountryCode(values.country)[0].state_code)
            }
        }, [values, initialValues, setFieldValue])

        return null
    }

    function getIntlCode(country: string) {
        return country === 'AU' ? ['+61'] : ['+64']
    }

    useEffect(() => {
        if (isSuccess && form.current) {
            const { id } = securityQuestions?.[0]!

            form.current.setFieldValue('security_question_id', id)
        }

        if (isError && form.current) {
            form.current.setSubmitting(false)
        }
    }, [form, isError, isSuccess, securityQuestions])

    useEffect(() => {
        if (registerSuccess) {
            refetch()
            dispatch(setCartOpen(false))
            dispatch(setCartItem({ ...cartItem, template: 'cart' }))
        }
    }, [registerSuccess])

    return (
        <Formik
            innerRef={form}
            initialValues={{
                firstname: '',
                lastname: '',
                phone: '',
                email: '',
                company_name: '',
                abn: '',
                address1: '',
                address2: '',
                city: '',
                postcode: '',
                state: 'VIC',
                country: 'AU',
                password: '',
                password_confirmation: '',
                dob: '',
                security_question_id: 0,
                security_answer: '',
                account_type: 'personal'
            }}
            validationSchema={Yup.object({
                firstname: Yup.string()
                    .required('Required'),
                lastname: Yup.string()
                    .required('Required'),
                phone: Yup.string()
                    .required('Required')
                    .matches(regex.phone, 'Invalid phone number'),
                email: Yup.string()
                    .email('Invalid email address')
                    .required('Required'),
                company_name: Yup.string()
                    .when('account_type', {
                        is: 'business',
                        then: Yup.string().required('Required')
                    }),
                abn: Yup.string()
                    .when('account_type', {
                        is: 'business',
                        then: Yup.string().required('Required')
                    }),
                address1: Yup.string()
                    .required('Required'),
                city: Yup.string()
                    .required('Required'),
                postcode: Yup.number()
                    .typeError('Invalid postcode')
                    .required('Required')
                    .min(1000, 'Invalid postcode')
                    .max(9999, 'Invalid postcode'),
                password: Yup.string()
                    .min(4)
                    .required('Required'),
                password_confirmation: Yup.string()
                    .oneOf([Yup.ref('password'), null], 'Password must match')
                    .required('Required'),
                dob: Yup.string()
                    .required('Required')
                    .matches(regex.dateOfBirth, 'Please enter a valid date in the format DD/MM/YYYY'),
                security_answer: Yup.string()
                    .required('Required')
            })}
            onSubmit={(values) => {
                register(values)
            }}
        >
            {({ isSubmitting, values }) => (
                <Form id={'signupForm'}>
                    <Row>
                        <Field
                            label={'First Name'}
                            name={'firstname'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            label={'Last Name'}
                            name={'lastname'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                    </Row>
                    <Field
                        label={'Phone Number'}
                        name={'phone'}
                        type={'text'}
                        prefixes={getIntlCode(values.country)}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        label={'Email Address'}
                        name={'email'}
                        type={'text'}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        id={'account'}
                        label={'Account Type'}
                        name={'account_type'}
                        type={'select'}
                        disabled={isSubmitting}
                        list={[
                            { value: 'business', label: 'Business' },
                            { value: 'personal', label: 'Personal' }
                        ]}
                        component={NXUIForm.SelectField}
                    />
                    {values.account_type === 'business' &&
                        (
                            <>
                                <Field
                                    label={'Company Name'}
                                    name={'company_name'}
                                    type={'text'}
                                    disabled={isSubmitting}
                                    component={NXUIForm.InputField}
                                />
                                <Field
                                    label={'ABN'}
                                    name={'abn'}
                                    type={'text'}
                                    disabled={isSubmitting}
                                    component={NXUIForm.InputField}
                                />
                            </>
                        )
                    }
                    <Field
                        label={'Address 1'}
                        name={'address1'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        label={'Address 2'}
                        name={'address2'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Row>
                        <Field
                            label={'Suburb'}
                            name={'city'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            id={'postcode'}
                            label={'Postcode'}
                            name={'postcode'}
                            type={'text'}
                            disabled={isSubmitting}
                            autoComplete={'postal-code'}
                            component={NXUIForm.InputField}
                        />
                    </Row>
                    <Row>
                        <Field
                            id={'state'}
                            label={'State'}
                            name={'state'}
                            type={'select'}
                            disabled={isSubmitting}
                            list={getStatesByCountryCode(values.country).map(({ state_code, state }) => (
                                { label: state, value: state_code }
                            ))}
                            component={NXUIForm.SelectField}
                        />
                        <Field
                            id={'country'}
                            label={'Country'}
                            name={'country'}
                            type={'select'}
                            disabled={isSubmitting}
                            list={[
                                { value: 'AU', label: 'Australia' },
                                { value: 'NZ', label: 'New Zealand' }
                            ]}
                            component={NXUIForm.SelectField}
                        />
                    </Row>
                    <Row>
                        <Field
                            label={'Password'}
                            name={'password'}
                            type={'password'}
                            autoComplete={'current-password'}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            label={'Confirm Password'}
                            name={'password_confirmation'}
                            type={'password'}
                            autoComplete={'current-password'}
                            component={NXUIForm.InputField}
                        />
                    </Row>
                    <Field
                        label={'Date of Birth'}
                        placeholder={'DD/MM/YYYY'}
                        name={'dob'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        id={'questions'}
                        label={'Security Questions'}
                        name={'security_question_id'}
                        type={'select'}
                        disabled={isSubmitting}
                        list={securityQuestions
                            ? securityQuestions.map(({ id, question }: { id: number, question: string }) => {
                                return {
                                    value: id,
                                    label: question
                                }
                            })
                            : []
                        }
                        component={NXUIForm.SelectField}
                    />
                    <Field
                        label={'Security Answer'}
                        name={'security_answer'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <UpdateState/>
                </Form>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Row = styled.div`
    display: flex;
    justify-content: space-between;
    ${props => Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm'])} {
        flex-direction: column;
    };

    > div {
        width: 210px;
        ${props => Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm'])} {
            width: 100%;
        };
    }
`
